import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Cpu, Mic, Brain, TrendingUp, Clock, Award, BarChart3 } from '../../components/icons';
import { supabase } from '../../lib/supabase';
import { isUserAdmin } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface UsageLog {
  id: string;
  model_variant: string | null;
  backend_model: string | null;
  reasoning_effort: string | null;
  module: string | null;
  practice_style: string | null;
  type: string;
  created_at: string;
}

interface SessionRow {
  id: string;
  module: string;
  practice_style: string;
  mode: string;
  duration_seconds: number;
  score: number | null;
  status: string;
  model_variant: string | null;
  created_at: string;
}

interface ModelStats {
  variant: string;
  label: string;
  sessionCount: number;
  voiceCount: number;
  chatCount: number;
  avgScore: number | null;
  avgDuration: number | null;
  modules: Record<string, number>;
}

export function NLPModelABTest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }

      const admin = await isUserAdmin(user.id);
      if (!admin) { navigate('/login'); return; }

      const [logsRes, sessionsRes] = await Promise.all([
        supabase.from('nlp_practice_usage_logs').select('*').order('created_at', { ascending: false }).limit(500),
        supabase.from('nlp_practice_sessions').select('*').order('created_at', { ascending: false }).limit(500),
      ]);

      setUsageLogs((logsRes.data as unknown as UsageLog[]) || []);
      setSessions((sessionsRes.data as unknown as SessionRow[]) || []);
      setLoading(false);
    };
    init();
  }, [navigate]);

  if (loading) return <LoadingSpinner message="Loading A/B test data..." />;

  const buildStats = (variant: string, label: string): ModelStats => {
    const logs = usageLogs.filter(l => l.model_variant === variant);
    const sess = sessions.filter(s => s.model_variant === variant);
    const completed = sess.filter(s => s.status === 'completed' && s.score !== null);
    const voiceLogs = logs.filter(l => l.type === 'voice');
    const chatLogs = logs.filter(l => l.type === 'chat');
    const durations = sess.filter(s => s.duration_seconds > 0);

    const modules: Record<string, number> = {};
    sess.forEach(s => {
      const key = s.module || 'unknown';
      modules[key] = (modules[key] || 0) + 1;
    });

    return {
      variant,
      label,
      sessionCount: sess.length,
      voiceCount: voiceLogs.length,
      chatCount: chatLogs.length,
      avgScore: completed.length > 0 ? Math.round(completed.reduce((sum, s) => sum + (s.score || 0), 0) / completed.length) : null,
      avgDuration: durations.length > 0 ? Math.round(durations.reduce((sum, s) => sum + s.duration_seconds, 0) / durations.length) : null,
      modules,
    };
  };

  const statsA = buildStats('gpt-realtime-2.1', 'gpt-realtime-2.1');
  const statsB = buildStats('gpt-live-1', 'gpt-live-1 (5.6-terra)');
  const noVariant = usageLogs.filter(l => !l.model_variant).length;

  const StatCard = ({ stats, color }: { stats: ModelStats; color: string }) => (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className={`p-2.5 ${color} rounded-xl`}>
          <Cpu className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-80">{stats.label}</h3>
          <p className="text-xs text-gray-40">{stats.sessionCount} sessions · {stats.voiceCount} voice · {stats.chatCount} chat</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="bg-brand-5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-gray-40" />
            <span className="text-xs text-gray-40">Avg Score</span>
          </div>
          <div className="text-2xl font-bold text-gray-80">
            {stats.avgScore !== null ? `${stats.avgScore}/100` : '—'}
          </div>
        </div>
        <div className="bg-brand-5 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-gray-40" />
            <span className="text-xs text-gray-40">Avg Duration</span>
          </div>
          <div className="text-2xl font-bold text-gray-80">
            {stats.avgDuration !== null ? `${Math.floor(stats.avgDuration / 60)}m ${stats.avgDuration % 60}s` : '—'}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-40 uppercase tracking-wide mb-2">Sessions by Module</p>
        {Object.entries(stats.modules).length === 0 ? (
          <p className="text-sm text-gray-40">No sessions yet</p>
        ) : (
          Object.entries(stats.modules).map(([mod, count]) => (
            <div key={mod} className="flex items-center justify-between text-sm">
              <span className="text-gray-60 capitalize">{mod === 'meta_model' ? 'Meta Model' : 'Presuppositions'}</span>
              <span className="font-semibold text-gray-80">{count}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-gray-60 hover:text-gray-80 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin Dashboard
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <BarChart3 className="w-5 h-5 text-brand-60" />
            <h1 className="text-heading-xs text-gray-80">NLP Model A/B Test</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-text-lg text-gray-80 mb-2">Model Comparison</h2>
          <p className="text-sm text-gray-40">
            Side-by-side comparison of OpenAI realtime models used in NLP Practice voice sessions.
            Use the practice page to run sessions with each model, then review the results here.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <StatCard stats={statsA} color="bg-brand-60" />
          <StatCard stats={statsB} color="bg-success-50" />
        </div>

        {noVariant > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-700">
              {noVariant} usage log(s) have no model variant recorded (pre-date the A/B test feature).
            </p>
          </div>
        )}

        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-brand-60" />
            <h3 className="text-text-md text-gray-80 font-semibold">Recent Sessions</h3>
          </div>
          {sessions.length === 0 ? (
            <div className="py-8 text-center">
              <Brain className="w-12 h-12 text-gray-20 mx-auto mb-3" />
              <p className="text-gray-40 text-sm">No sessions recorded yet. Start testing from the practice page.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-20 text-left">
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Date</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Module</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Style</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Mode</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Model</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Score</th>
                    <th className="pb-3 text-xs font-semibold text-gray-40 uppercase tracking-wide">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.slice(0, 20).map((s) => (
                    <tr key={s.id} className="border-b border-gray-10">
                      <td className="py-3 text-gray-60">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="py-3 text-gray-60">{s.module === 'meta_model' ? 'Meta Model' : 'Presup.'}</td>
                      <td className="py-3 text-gray-60 capitalize">{s.practice_style}</td>
                      <td className="py-3">
                        <span className="inline-flex items-center gap-1 text-gray-60">
                          {s.mode === 'voice' ? <Mic className="w-3.5 h-3.5" /> : null}
                          {s.mode}
                        </span>
                      </td>
                      <td className="py-3">
                        {s.model_variant ? (
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            s.model_variant === 'gpt-live-1' ? 'bg-success-10 text-success-50' : 'bg-brand-10 text-brand-60'
                          }`}>
                            {s.model_variant}
                          </span>
                        ) : (
                          <span className="text-gray-30 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 text-gray-60">{s.score !== null ? `${s.score}/100` : '—'}</td>
                      <td className="py-3 text-gray-60">
                        {s.duration_seconds > 0 ? `${Math.floor(s.duration_seconds / 60)}m ${s.duration_seconds % 60}s` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6">
          <button
            onClick={() => navigate('/admin/nlp-model-test/practice')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-60 text-white rounded-xl font-semibold hover:bg-brand-70 transition-all"
          >
            <Mic className="w-5 h-5" />
            Go to Practice Page
          </button>
        </div>
      </main>
    </div>
  );
}
